import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api';
import InformeIvaModal from './InformeIvaModal';

const MESES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

const TAB_FILTROS = [
  { id: '', label: 'Todos' },
  { id: 'sin_datos', label: 'Falta datos SII' },
  { id: 'sin_informe', label: 'Falta informe' },
  { id: 'revision', label: 'Revisar' },
  { id: 'listo', label: 'Listos' },
  { id: 'error', label: 'Problemas' }
];

const ESTADO_STYLE = {
  sin_datos: 'bg-slate-100 text-slate-800 border-slate-200',
  sin_informe: 'bg-sky-50 text-sky-900 border-sky-200',
  procesando: 'bg-amber-50 text-amber-900 border-amber-200',
  revision: 'bg-orange-50 text-orange-900 border-orange-200',
  listo: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  error: 'bg-red-50 text-red-900 border-red-200'
};

const RUN_TERMINAL = new Set([
  'READY',
  'WAITING_APPROVAL',
  'BLOCKED',
  'FAILED',
  'CANCELLED',
  'SENT',
  'APPROVED'
]);

const TOASTED_RUN_FAILURES_KEY = 'informeIvaToastedRunFailures';

function shouldToastRunFailure(runId) {
  if (!runId) return false;
  try {
    const raw = sessionStorage.getItem(TOASTED_RUN_FAILURES_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return !ids.includes(String(runId));
  } catch {
    return true;
  }
}

function markRunFailureToasted(runId) {
  if (!runId) return;
  try {
    const raw = sessionStorage.getItem(TOASTED_RUN_FAILURES_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    const next = [...ids.filter((id) => id !== String(runId)), String(runId)].slice(-200);
    sessionStorage.setItem(TOASTED_RUN_FAILURES_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/** Un toast por run fallido por sesión de navegador (no repetir al refrescar el dashboard). */
function toastRunFailureOnce(runId, nombre, message) {
  if (!shouldToastRunFailure(runId)) return;
  markRunFailureToasted(runId);
  toast.error(`${nombre}: ${message || 'Informe IVA falló'}`, { duration: 12000 });
}

const RUN_STATUS_ES = {
  QUEUED: 'En cola — iniciará en breve',
  PENDING: 'Iniciando informe integral',
  SYNCING: 'Paso 1/2: un login SII → RCV, honorarios y F29 (F29 es lo más lento)',
  VALIDATING_DATA: 'Validando datos RCV',
  CALCULATING: 'Paso 2/2: calculando IVA y proyección F29',
  VALIDATING_RESULT: 'Revisando resultado',
  GENERATING: 'Generando informe y PDF'
};

function mesAnioCalendario(date = new Date()) {
  return { mes: date.getMonth() + 1, anio: date.getFullYear() };
}

const fmtMonto = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0
  }).format(n);
};

function datosResumen(row) {
  const v = row.rcvFilas?.ventas || 0;
  const c = row.rcvFilas?.compras || 0;
  if (row.syncInProgress) return 'Actualizando datos en el SII…';
  if (v + c === 0) return 'Sin resumen RCV guardado — use Informe IVA para actualizar';
  return `RCV en sistema: ${v} tipos venta · ${c} tipos compra (tabla resumen SII)`;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const ACTIVE_RUN_STORAGE_KEY = 'informeIvaActiveRun';
/** 4 h — el sync SII+F29 puede superar 20 min; el poll no debe “fallar” antes que el servidor */
const POLL_MAX_MS = 4 * 60 * 60 * 1000;

function persistActiveRun(payload) {
  try {
    sessionStorage.setItem(ACTIVE_RUN_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota / private mode */
  }
}

function clearActiveRunStorage() {
  try {
    sessionStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
  } catch {
    /* */
  }
}

function readActiveRunStorage() {
  try {
    const raw = sessionStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function formatElapsed(startMs) {
  if (!startMs) return '0:00';
  const s = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

const PIPELINE_STEPS = [
  { id: 'sync', label: 'Datos SII', statuses: ['SYNCING'] },
  { id: 'valid', label: 'Validar', statuses: ['VALIDATING_DATA', 'VALIDATING_RESULT'] },
  { id: 'calc', label: 'Calcular IVA', statuses: ['CALCULATING'] },
  { id: 'gen', label: 'Informe / PDF', statuses: ['GENERATING'] },
  { id: 'done', label: 'Listo', statuses: ['READY', 'WAITING_APPROVAL', 'APPROVED', 'SENT'] }
];

function stepIndexForStatus(status) {
  if (!status) return 0;
  if (['QUEUED', 'PENDING'].includes(status)) return 0;
  for (let i = 0; i < PIPELINE_STEPS.length; i++) {
    if (PIPELINE_STEPS[i].statuses.includes(status)) return i;
  }
  if (RUN_TERMINAL.has(status)) return PIPELINE_STEPS.length - 1;
  return 0;
}

export default function PanelInformesIva() {
  const [{ mes, anio }, setPeriodo] = useState(() => mesAnioCalendario());
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [listPage, setListPage] = useState(1);
  const [tab, setTab] = useState('');
  const [busyCliente, setBusyCliente] = useState(null);
  const [job, setJob] = useState(null);
  const [clock, setClock] = useState(Date.now());
  const [detalleRun, setDetalleRun] = useState(null);
  const [configCliente, setConfigCliente] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [ppmRates, setPpmRates] = useState([]);
  const [nuevaTasaPpm, setNuevaTasaPpm] = useState({ tasa: '', desde: '', hasta: '' });
  const [preInformeModal, setPreInformeModal] = useState(null);
  const [preInformeLoading, setPreInformeLoading] = useState(false);
  const [modalComplementos, setModalComplementos] = useState(null);
  const [iuscInput, setIuscInput] = useState('');
  const [rem504Input, setRem504Input] = useState('');
  const [ppmPctInput, setPpmPctInput] = useState('');
  const [ppmMontoInput, setPpmMontoInput] = useState('');
  const resumeRunIdRef = useRef(null);

  const loadDashboard = useCallback(
    async (silent = false, pageOverride) => {
      if (!silent) setLoading(true);
      try {
        const page = pageOverride ?? listPage;
        const { data } = await api.get(`/informes-iva/dashboard/${anio}/${mes}`, {
          params: {
            page,
            limit: 20,
            q: filtro.trim() || undefined,
            tab: tab || undefined
          }
        });
        setDashboard(data);
        if (data.pagination?.page) setListPage(data.pagination.page);
      } catch (e) {
        toast.error(e.response?.data?.error || e.message);
      } finally {
        setLoading(false);
      }
    },
    [anio, mes, filtro, tab, listPage]
  );

  useEffect(() => {
    setListPage(1);
  }, [mes, anio, tab]);

  useEffect(() => {
    const t = setTimeout(() => loadDashboard(true, listPage), filtro ? 350 : 0);
    return () => clearTimeout(t);
  }, [loadDashboard, listPage, filtro, tab, mes, anio]);

  useEffect(() => {
    const hasActive = (dashboard?.items || []).some((i) => {
      if (i.syncInProgress) return true;
      const r = i.lastRun;
      if (!r || r.mes !== mes || r.anio !== anio) return false;
      return r.status && !RUN_TERMINAL.has(r.status);
    });
    if (!hasActive) return undefined;
    const id = setInterval(() => loadDashboard(true), 12000);
    return () => clearInterval(id);
  }, [dashboard, mes, anio, loadDashboard]);

  useEffect(() => {
    if (!job || job.done) return undefined;
    const t = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(t);
  }, [job]);

  const nombreCliente = useCallback(
    (clienteId) => {
      const it = dashboard?.items?.find((i) => String(i.cliente._id) === String(clienteId));
      return it?.cliente?.nombre || 'Empresa';
    },
    [dashboard]
  );

  const updateJobFromRun = (run) => {
    if (!run) return;
    setJob((j) =>
      j
        ? {
            ...j,
            status: run.status,
            logs: run.logEntries || [],
            error: run.errorMessage ?? j.error,
            iva: run.estimatedVatDetermined ?? j.iva,
            totalF29: run.totalMensualEstimadoF29 ?? j.totalF29,
            startedAtMs: run.startedAt ? new Date(run.startedAt).getTime() : j.startedAtMs
          }
        : j
    );
  };

  const pollRunUntilDone = async (runId, maxMs = POLL_MAX_MS) => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const { data } = await api.get(`/informes-iva/run/${runId}?lite=1`);
      updateJobFromRun(data.run);
      if (data.run?.status && RUN_TERMINAL.has(data.run.status)) {
        return data;
      }
      await sleep(2000);
    }
    const { data: last } = await api.get(`/informes-iva/run/${runId}?lite=1`);
    updateJobFromRun(last.run);
    if (last.run?.status && RUN_TERMINAL.has(last.run.status)) {
      return last;
    }
    throw new Error(
      'El servidor sigue procesando tras varias horas. Use «Cancelar proceso» o actualice la lista; si falló, debería verse en «Problemas».'
    );
  };

  const verPreview = (runId) => {
    const url = `${window.location.origin}/informes-iva/ver/${runId}`;
    window.open(url, '_blank', 'noopener');
  };

  const cancelarProcesoInforme = async () => {
    if (!job) return;
    const ok = window.confirm(
      '¿Detener este Informe IVA? Se liberará el estado en el servidor. Si el SII sigue en otra ventana, puede tardar unos segundos en abortar.'
    );
    if (!ok) return;
    try {
      if (job.runId) {
        await api.post(`/informes-iva/run/${job.runId}/cancel`);
      } else if (job.clienteId) {
        await api.post(`/informes-iva/cancel-active/${job.clienteId}/${anio}/${mes}`);
      }
      setJob((j) => ({
        ...j,
        done: true,
        success: false,
        status: 'CANCELLED',
        error: 'Proceso cancelado. Puede iniciar Informe IVA de nuevo.'
      }));
      setBusyCliente(null);
      clearActiveRunStorage();
      toast.message('Proceso cancelado');
      await loadDashboard(true);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const finishRunJob = async (runId, runStatus, nombreEmpresa) => {
    clearActiveRunStorage();
    await loadDashboard(true);
    if (runStatus === 'CANCELLED') {
      setJob((j) => ({
        ...j,
        done: true,
        success: false,
        status: 'CANCELLED',
        error: 'Informe cancelado.'
      }));
      setBusyCliente(null);
      toast.message('Informe IVA cancelado');
      return;
    }
    if (['READY', 'WAITING_APPROVAL', 'APPROVED', 'SENT'].includes(runStatus)) {
      setJob((j) => ({
        ...j,
        done: true,
        success: true,
        status: runStatus,
        runId
      }));
      toast.success('Informe generado — abriendo vista…');
      verPreview(runId);
      return;
    }
    let errMsg = 'No se pudo completar el informe';
    try {
      const { data } = await api.get(`/informes-iva/run/${runId}?lite=1`);
      errMsg = data.run?.errorMessage || errMsg;
      setJob((j) => ({
        ...j,
        done: true,
        success: false,
        status: runStatus,
        runId,
        error: errMsg,
        logs: data.run?.logEntries || j.logs
      }));
    } catch {
      setJob((j) => ({ ...j, done: true, success: false, status: runStatus, runId, error: errMsg }));
    }
    try {
      const { data } = await api.get(`/informes-iva/run/${runId}`);
      setDetalleRun(data);
    } catch {
      /* */
    }
    toastRunFailureOnce(runId, nombreEmpresa || job?.nombre || 'Empresa', errMsg);
  };

  const items = dashboard?.items || [];
  const pagination = dashboard?.pagination;

  const generarInforme = async (clienteId, { autoSync = true, syncF29 = true, nombre: nombreOverride } = {}) => {
    setBusyCliente(clienteId);
    const nombre = nombreOverride || nombreCliente(clienteId);
    setJob({
      kind: 'run',
      clienteId,
      nombre,
      runId: null,
      startedAtMs: Date.now(),
      status: 'SYNCING',
      logs: [
        'Informe IVA integral: actualización SII (ventas, compras, honorarios, F29) + cálculo + PDF'
      ],
      done: false,
      autoSync: true
    });
    let runIdTracked = null;
    try {
      const body = {
        mes,
        anio,
        generatePdf: true,
        autoSync: autoSync !== false,
        syncF29: syncF29 !== false,
        wait: false
      };
      const { data: accepted } = await api.post(`/informes-iva/run/${clienteId}`, body);
      const runId = accepted.runId;
      if (!runId) throw new Error('Sin respuesta del servidor');
      runIdTracked = String(runId);

      setJob((j) => ({ ...j, runId: runIdTracked }));
      persistActiveRun({
        runId: runIdTracked,
        clienteId: String(clienteId),
        nombre,
        mes,
        anio,
        startedAtMs: Date.now()
      });

      if (accepted.alreadyRunning) {
        toast.info('Ya había un proceso en curso — mostrando el avance real');
      }

      if (accepted.async || body.wait !== true) {
        const result = await pollRunUntilDone(runId);
        await finishRunJob(runId, result.run?.status, nombre);
        return;
      }

      const st = accepted.status;
      updateJobFromRun({
        status: st,
        logEntries: [`Estado final: ${st}`],
        estimatedVatDetermined: accepted.ivaDeterminado
      });
      await finishRunJob(runId, st, nombre);
    } catch (e) {
      if (runIdTracked) {
        try {
          const { data } = await api.get(`/informes-iva/run/${runIdTracked}?lite=1`);
          if (data.run?.status && RUN_TERMINAL.has(data.run.status)) {
            await finishRunJob(runIdTracked, data.run.status, nombre);
            return;
          }
          setJob((j) => ({
            ...j,
            done: true,
            success: false,
            error: data.run?.errorMessage || e.message,
            logs: data.run?.logEntries || j?.logs,
            status: data.run?.status
          }));
          toastRunFailureOnce(runIdTracked, nombre, data.run?.errorMessage || e.message);
          return;
        } catch {
          /* */
        }
      }
      setJob((j) => ({ ...j, done: true, success: false, error: e.message }));
      toast.error(e.response?.data?.error || e.message, { duration: 12000 });
    } finally {
      setBusyCliente(null);
    }
  };

  useEffect(() => {
    if (job) return;
    const stored = readActiveRunStorage();
    if (!stored?.runId || stored.mes !== mes || stored.anio !== anio) return;
    if (resumeRunIdRef.current === stored.runId) return;
    resumeRunIdRef.current = stored.runId;

    (async () => {
      try {
        const { data } = await api.get(`/informes-iva/run/${stored.runId}?lite=1`);
        const st = data.run?.status;
        if (st && RUN_TERMINAL.has(st)) {
          clearActiveRunStorage();
          if (['FAILED', 'BLOCKED'].includes(st)) {
            toastRunFailureOnce(stored.runId, stored.nombre || 'Empresa', data.run.errorMessage);
          }
          await loadDashboard(true);
          return;
        }
        setBusyCliente(stored.clienteId);
        setJob({
          kind: 'run',
          clienteId: stored.clienteId,
          nombre: stored.nombre || 'Empresa',
          runId: stored.runId,
          startedAtMs: stored.startedAtMs || Date.now(),
          status: st || 'SYNCING',
          logs: data.run?.logEntries || ['Reanudando seguimiento del informe en curso…'],
          done: false,
          autoSync: true
        });
        toast.message(`Siguiendo informe en curso: ${stored.nombre}`);
        const result = await pollRunUntilDone(stored.runId);
        await finishRunJob(stored.runId, result.run?.status, stored.nombre);
      } catch (e) {
        toast.error(e.message || 'Error al reanudar seguimiento');
      } finally {
        setBusyCliente(null);
      }
    })();
  }, [job, mes, anio, loadDashboard]);

  const verDetalleRun = async (runId) => {
    try {
      const { data } = await api.get(`/informes-iva/run/${runId}`);
      setDetalleRun(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const descargarPdf = async (runId) => {
    try {
      const res = await api.get(`/informes-iva/pdf/${runId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe-iva-${runId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo descargar PDF');
    }
  };

  const aprobar = async (runId) => {
    try {
      await api.post(`/informes-iva/approve/${runId}`);
      toast.success('Informe aprobado');
      await loadDashboard(true);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const enviar = async (run, { reenvio = false } = {}) => {
    const runId = run._id;
    const needsForce =
      run.status === 'WAITING_APPROVAL' ||
      run.confidence === 'LOW' ||
      run.status === 'SENT' ||
      reenvio;
    let msg = '¿Enviar el informe al email configurado del cliente?';
    if (run.status === 'WAITING_APPROVAL') {
      msg = 'El informe no está aprobado. ¿Enviar igual al cliente?';
    } else if (run.confidence === 'LOW') {
      msg = 'Confianza baja. ¿Enviar igual?';
    } else if (run.status === 'SENT') {
      msg = 'Este informe ya fue enviado. ¿Reenviar?';
    }
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/informes-iva/send/${runId}`, { force: needsForce });
      toast.success(run.status === 'SENT' ? 'Informe reenviado' : 'Informe enviado');
      await loadDashboard(true);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const cargarComplementos = async (clienteId) => {
    const { data } = await api.get(`/informes-iva/complementos/${clienteId}/${anio}/${mes}`);
    return data;
  };

  const abrirPreInforme = async (row) => {
    setPreInformeModal(row);
    setPreInformeLoading(true);
    setModalComplementos(null);
    setIuscInput('');
    setRem504Input('');
    setPpmPctInput('');
    setPpmMontoInput('');
    try {
      const data = await cargarComplementos(row.cliente._id);
      setModalComplementos(data);
      setIuscInput(data?.iusc?.current?.amount ?? '');
      setPpmPctInput(
        data?.ppm?.porcentaje != null && data.ppm.porcentaje !== ''
          ? String(data.ppm.porcentaje)
          : data?.ppm?.tasaVigente != null
            ? String(data.ppm.tasaVigente)
            : ''
      );
      setPpmMontoInput(
        data?.ppm?.montoManual != null && data.ppm.montoManual !== '' ? String(data.ppm.montoManual) : ''
      );
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
      setPreInformeModal(null);
    } finally {
      setPreInformeLoading(false);
    }
  };

  const confirmarInformeIva = async () => {
    if (!preInformeModal) return;
    const clienteId = preInformeModal.cliente._id;
    try {
      if (rem504Input !== '' && modalComplementos?.remanente504?.status !== 'CONFIRMED') {
        await api.put(`/informes-iva/complementos/${clienteId}/${anio}/${mes}/remanente`, {
          amount: Number(rem504Input)
        });
      }
      if (
        modalComplementos?.iusc?.enabled !== false &&
        iuscInput !== '' &&
        iuscInput != null &&
        (modalComplementos.iusc.pending || iuscInput !== modalComplementos.iusc.current?.amount)
      ) {
        await api.put(`/informes-iva/complementos/${clienteId}/${anio}/${mes}/iusc`, {
          amount: Number(iuscInput)
        });
      }
      if (ppmPctInput !== '' || ppmMontoInput !== '') {
        const ppmPayload = {};
        if (ppmPctInput !== '') ppmPayload.ppmPorcentaje = Number(ppmPctInput);
        if (ppmMontoInput !== '') ppmPayload.ppmMontoManual = Number(ppmMontoInput);
        await api.put(`/informes-iva/complementos/${clienteId}/${anio}/${mes}/ppm`, ppmPayload);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
      return;
    }
    const nombre = preInformeModal.cliente.nombre;
    setPreInformeModal(null);
    await generarInforme(clienteId, { autoSync: true, syncF29: true, nombre });
  };

  const abrirConfig = async (clienteId) => {
    try {
      const [{ data }, { data: del }, { data: rates }] = await Promise.all([
        api.get(`/informes-iva/config/cliente/${clienteId}`),
        api.get(`/informes-iva/deliveries/${clienteId}`),
        api.get(`/informes-iva/ppm-rates/${clienteId}`)
      ]);
      setConfigCliente({ ...data, clienteId });
      setDeliveries(del || []);
      setPpmRates(rates || []);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const guardarConfig = async () => {
    if (!configCliente) return;
    try {
      await api.put(`/informes-iva/config/cliente/${configCliente.clienteId}`, {
        activoInformes: configCliente.activoInformes,
        emailCliente: configCliente.emailCliente,
        notaContador: configCliente.notaContador,
        remanenteManual: configCliente.remanenteManual,
        ppmPorcentaje: configCliente.ppmPorcentaje,
        ppmMontoManual: configCliente.ppmMontoManual,
        ppmBasisMode: configCliente.ppmBasisMode,
        ppmBasisManual: configCliente.ppmBasisManual,
        monthlyTaxConfig: configCliente.monthlyTaxConfig,
        autoSendEnabled: configCliente.autoSendEnabled,
        modoEnvio: configCliente.modoEnvio,
        approvalRequired: configCliente.approvalRequired,
        frecuencia: configCliente.frecuencia,
        ventanaDatos: configCliente.ventanaDatos
      });
      toast.success('Configuración guardada');
      setConfigCliente(null);
      loadDashboard(true);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const accionesFila = (row) => {
    const id = row.cliente._id;
    const busy = busyCliente === id;
    const e = row.estado?.key;
    const run = row.lastRun;
    const runMes = run?.mes === mes && run?.anio === anio;
    const tieneInforme =
      runMes && ['READY', 'WAITING_APPROVAL', 'APPROVED', 'SENT'].includes(run?.status);
    const btn =
      'px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 whitespace-nowrap';

    if (busy || e === 'procesando' || row.syncInProgress) {
      return (
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs text-amber-800">En curso…</span>
          <button
            type="button"
            className={`${btn} text-red-700 hover:bg-red-50`}
            onClick={async () => {
              if (!window.confirm('¿Cancelar el proceso en curso para esta empresa?')) return;
              try {
                await api.post(`/informes-iva/cancel-active/${id}/${anio}/${mes}`);
                toast.message('Proceso cancelado');
                await loadDashboard(true);
                if (job?.clienteId === id) {
                  setJob(null);
                  setBusyCliente(null);
                }
              } catch (err) {
                toast.error(err.response?.data?.error || err.message);
              }
            }}
          >
            Cancelar
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-wrap justify-end gap-1.5 max-w-md">
        <button
          type="button"
          onClick={() => abrirPreInforme(row)}
          className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`}
        >
          {e === 'error' && runMes ? 'Reintentar' : 'Informe IVA'}
        </button>
        {e === 'error' && runMes && (
          <button type="button" onClick={() => verDetalleRun(run._id)} className={`${btn} border border-red-200 text-red-800`}>
            Error
          </button>
        )}
        {tieneInforme && (
          <>
            <button type="button" onClick={() => verPreview(run._id)} className={`${btn} border border-slate-200 hover:bg-slate-50`}>
              Ver
            </button>
            {run.hasPdf !== false && (
              <button type="button" onClick={() => descargarPdf(run._id)} className={`${btn} border border-slate-200 hover:bg-slate-50`}>
                PDF
              </button>
            )}
            {run.status === 'WAITING_APPROVAL' && (
              <button type="button" onClick={() => aprobar(run._id)} className={`${btn} bg-violet-600 text-white hover:bg-violet-700`}>
                Aprobar
              </button>
            )}
            {run.status === 'SENT' ? (
              <button type="button" onClick={() => enviar(run, { reenvio: true })} className={`${btn} border border-amber-300 text-amber-900`}>
                Reenviar
              </button>
            ) : (
              ['READY', 'APPROVED', 'WAITING_APPROVAL'].includes(run.status) && (
                <button type="button" onClick={() => enviar(run)} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`}>
                  Email
                </button>
              )
            )}
          </>
        )}
        <button type="button" onClick={() => abrirConfig(id)} className={`${btn} text-slate-600 hover:bg-slate-100`}>
          Cliente
        </button>
      </div>
    );
  };

  const stats = dashboard?.stats;
  const tabCounts = dashboard?.tabCounts || {};
  const pag = dashboard?.pagination;
  const fmtF29Code = (v) => (v == null || v === '' ? '—' : fmtMonto(Number(v)));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-20">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Informes IVA</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Panel del mes</h1>
          <p className="text-slate-600 mt-2 max-w-2xl text-sm leading-relaxed">
            Empresas con IVA activo. Use el menú superior para configuración, correo SMTP y opciones globales. Aquí solo
            genera informes y envíos por cliente.
          </p>

          <div className="mt-5 flex flex-wrap items-end gap-3 p-4 bg-white/90 backdrop-blur border border-slate-200/80 rounded-2xl shadow-sm">
            <label className="text-sm">
              <span className="block text-slate-500 text-xs mb-1">Mes tributario</span>
              <select
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm min-w-[8rem]"
                value={mes}
                onChange={(e) => setPeriodo((p) => ({ ...p, mes: Number(e.target.value) }))}
              >
                {MESES.map((m, i) =>
                  i === 0 ? null : (
                    <option key={i} value={i}>
                      {m}
                    </option>
                  )
                )}
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-slate-500 text-xs mb-1">Año</span>
              <input
                type="number"
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-24"
                value={anio}
                onChange={(e) => setPeriodo((p) => ({ ...p, anio: Number(e.target.value) }))}
              />
            </label>
            <button
              type="button"
              onClick={() => loadDashboard()}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 hover:bg-slate-50"
            >
              Actualizar
            </button>
            {stats && (
              <div className="ml-auto flex flex-wrap gap-2 text-xs text-slate-600">
                <span className="px-2 py-1 rounded-md bg-slate-100">{stats.total} empresas IVA</span>
                <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-900">{stats.listos} listas</span>
                <span className="px-2 py-1 rounded-md bg-orange-50 text-orange-900">{stats.pendientes} revisión</span>
              </div>
            )}
          </div>
        </header>

        <div className="flex flex-wrap gap-2 mb-4">
          {TAB_FILTROS.map((t) => (
            <button
              key={t.id || 'all'}
              type="button"
              onClick={() => {
              setTab(t.id);
              setListPage(1);
            }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                tab === t.id
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
              }`}
            >
              {t.label}
              {dashboard ? ` · ${tabCounts[t.id] ?? 0}` : ''}
            </button>
          ))}
        </div>

        <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
          <input
            type="search"
            placeholder="Buscar por nombre, RUT o asignado…"
            className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
          {pag && (
            <p className="text-sm text-slate-500 shrink-0">
              {pag.total} resultados · pág. {pag.page}/{pag.totalPages}
            </p>
          )}
        </div>

        {loading && !dashboard && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 border-b border-slate-100 bg-slate-50 animate-pulse" />
            ))}
          </div>
        )}

        <div
          className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${loading && dashboard ? 'opacity-70' : ''}`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/80 text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3 font-semibold min-w-[200px]">Empresa</th>
                  <th className="px-3 py-3 font-semibold">Estado</th>
                  <th className="px-3 py-3 font-semibold hidden lg:table-cell">RCV</th>
                  <th className="px-3 py-3 font-semibold hidden md:table-cell">504 / 562 / 142</th>
                  <th className="px-3 py-3 font-semibold hidden xl:table-cell">IVA est.</th>
                  <th className="px-4 py-3 font-semibold text-right min-w-[220px]">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((row) => {
                  const c = row.cliente;
                  const est = row.estado || {};
                  const run = row.lastRun;
                  const style = ESTADO_STYLE[est.key] || ESTADO_STYLE.sin_datos;
                  const f29 = row.f29Sii;
                  return (
                    <tr key={c._id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 align-top">
                        <p className="font-medium text-slate-900 leading-snug">{c.nombre}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {c.rut}
                          {c.asignado ? ` · ${c.asignado}` : ''}
                        </p>
                        {est.hint && <p className="text-xs text-slate-500 mt-1">{est.hint}</p>}
                        {est.key === 'error' && run?.errorMessage && (
                          <p className="text-xs text-red-700 mt-1 line-clamp-2">{run.errorMessage}</p>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-md border ${style}`}>{est.label}</span>
                      </td>
                      <td className="px-3 py-3 align-top hidden lg:table-cell text-xs text-slate-600 max-w-[140px]">
                        {datosResumen(row)}
                      </td>
                      <td className="px-3 py-3 align-top hidden md:table-cell text-xs font-mono text-slate-700">
                        {f29 ? (
                          <>
                            <div>{fmtF29Code(f29.code504)}</div>
                            <div className="text-slate-500">{fmtF29Code(f29.code562)} · {fmtF29Code(f29.code142)}</div>
                          </>
                        ) : (
                          <span className="text-slate-400">Sin sync F29</span>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top hidden xl:table-cell text-xs">
                        {run?.mes === mes && run?.anio === anio && run?.estimatedVatDetermined != null ? (
                          <span className="font-medium text-slate-800">{fmtMonto(run.estimatedVatDetermined)}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right">{accionesFila(row)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && items.length === 0 && (
            <p className="text-center text-slate-500 py-16 text-sm">No hay empresas en este filtro.</p>
          )}
        </div>

        {pag && pag.totalPages > 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              disabled={pag.page <= 1}
              onClick={() => setListPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white disabled:opacity-40"
            >
              Anterior
            </button>
            {Array.from({ length: Math.min(pag.totalPages, 7) }, (_, i) => {
              let p;
              if (pag.totalPages <= 7) p = i + 1;
              else if (pag.page <= 4) p = i + 1;
              else if (pag.page >= pag.totalPages - 3) p = pag.totalPages - 6 + i;
              else p = pag.page - 3 + i;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setListPage(p)}
                  className={`min-w-[2.25rem] px-2 py-2 text-sm rounded-lg border ${
                    p === pag.page ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-200'
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              type="button"
              disabled={pag.page >= pag.totalPages}
              onClick={() => setListPage((p) => p + 1)}
              className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white disabled:opacity-40"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>

      {job && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Cerrar"
            onClick={() => {
              if (job.done) {
                setJob(null);
                setBusyCliente(null);
              }
            }}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-5 border-b bg-slate-50">
              <p className="text-xs uppercase tracking-wide text-slate-500">Informe IVA en proceso</p>
              <h2 className="text-lg font-semibold text-slate-900 mt-1">{job.nombre}</h2>
              <p className="text-sm text-slate-600 mt-1">
                {job.done
                  ? job.success
                    ? 'Proceso terminado'
                    : job.status === 'CANCELLED'
                      ? 'Proceso cancelado'
                      : 'Proceso con error'
                  : RUN_STATUS_ES[job.status] || job.status || 'En curso…'}
              </p>
              {!job.done && (
                <p className="text-sm font-mono text-indigo-700 mt-2">
                  Tiempo transcurrido: {formatElapsed(job.startedAtMs || clock)}
                </p>
              )}
            </div>

            {!job.done && job.kind === 'run' && (
              <div className="px-5 py-3 border-b flex flex-wrap gap-2 text-xs">
                {PIPELINE_STEPS.map((step, idx) => {
                  const active = idx <= stepIndexForStatus(job.status);
                  return (
                    <span
                      key={step.id}
                      className={`px-2 py-1 rounded-full ${active ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-400'}`}
                    >
                      {step.label}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="p-5 flex-1 overflow-y-auto min-h-[120px]">
              {!job.done && (
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  El servidor sigue trabajando — no cierre esta pestaña.
                </div>
              )}
              {job.done && job.success && job.runId && (
                <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="font-medium text-emerald-900">Informe disponible</p>
                  {job.iva != null && (
                    <p className="text-sm mt-1">IVA estimado: {fmtMonto(job.iva)}</p>
                  )}
                  <button
                    type="button"
                    className="mt-3 w-full py-2.5 bg-emerald-600 text-white rounded-lg font-medium"
                    onClick={() => verPreview(job.runId)}
                  >
                    Abrir informe
                  </button>
                  <button
                    type="button"
                    className="mt-2 w-full py-2 text-sm border rounded-lg"
                    onClick={() => descargarPdf(job.runId)}
                  >
                    Descargar PDF
                  </button>
                </div>
              )}
              {job.done && !job.success && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-900">
                  {job.error || 'Revise el detalle del error.'}
                  {job.runId && (
                    <button
                      type="button"
                      className="mt-2 block underline"
                      onClick={() => verDetalleRun(job.runId)}
                    >
                      Ver qué pasó
                    </button>
                  )}
                </div>
              )}
              <p className="text-xs font-medium text-slate-500 mb-2">Actividad reciente</p>
              <ul className="text-xs font-mono space-y-1 text-slate-700 max-h-48 overflow-y-auto bg-slate-50 rounded-lg p-3 border">
                {(job.logs || []).length === 0 && <li>Esperando respuesta del servidor…</li>}
                {(job.logs || []).slice(-15).map((line, i) => (
                  <li key={`${i}-${line.slice(0, 24)}`}>{line}</li>
                ))}
              </ul>
              {job.runId && !job.done && (
                <p className="text-[10px] text-slate-400 mt-2 break-all">ID seguimiento: {job.runId}</p>
              )}
            </div>

            <div className="p-4 border-t flex flex-wrap justify-end gap-2 items-center">
              {job.done ? (
                <button
                  type="button"
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm"
                  onClick={() => {
                    setJob(null);
                    setBusyCliente(null);
                  }}
                >
                  Cerrar
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm border rounded-lg text-slate-700"
                    onClick={() => setJob(null)}
                  >
                    Minimizar (sigue en servidor)
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm border border-red-300 text-red-800 rounded-lg hover:bg-red-50"
                    onClick={cancelarProcesoInforme}
                  >
                    Cancelar proceso
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <InformeIvaModal
        open={Boolean(preInformeModal)}
        onClose={() => setPreInformeModal(null)}
        maxWidth="max-w-md"
        title={
          <>
            <h2 className="text-lg font-semibold">Informe IVA — {preInformeModal?.cliente?.nombre}</h2>
            <p className="text-sm text-slate-600 mt-1">
              {MESES[mes]} {anio}: sync SII y generación del PDF.
            </p>
          </>
        }
        footer={
          <div className="flex gap-2 justify-end">
            <button type="button" className="px-4 py-2 text-sm border rounded-lg" onClick={() => setPreInformeModal(null)}>
              Cancelar
            </button>
            <button
              type="button"
              disabled={preInformeLoading || busyCliente === preInformeModal?.cliente?._id}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg disabled:opacity-50"
              onClick={confirmarInformeIva}
            >
              Iniciar Informe IVA
            </button>
          </div>
        }
      >
        {preInformeModal && (
          <>
            <p className="text-sm text-slate-600 sr-only">
              {MESES[mes]} {anio}: se actualizarán ventas, compras, honorarios y propuesta F29 en el SII, luego se
              calcula y genera el PDF.
            </p>
            {preInformeLoading ? (
              <p className="text-sm text-slate-500 mt-6">Cargando datos del período…</p>
            ) : (
              <div className="mt-5 space-y-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg border text-slate-600 space-y-1">
                  <p className="font-medium text-slate-800">Del SII al calcular</p>
                  <p>Ventas, compras, honorarios y (si aplica) propuesta F29.</p>
                  <p>
                    Remanente 504:{' '}
                    {modalComplementos?.remanente504?.status === 'CONFIRMED'
                      ? `${fmtMonto(modalComplementos.remanente504.amount)} (SII)`
                      : modalComplementos?.remanente504?.status === 'ESTIMATED'
                        ? `${fmtMonto(modalComplementos.remanente504.amount)} (mes anterior + UTM)`
                        : 'Se estima al calcular si hay mes anterior'}
                  </p>
                </div>
                {modalComplementos?.ppm?.enabled !== false && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-700">PPM 1ª categoría (cód. 62)</p>
                    <label className="block text-xs text-slate-600">
                      Tasa PPM (%)
                      <input
                        type="number"
                        step="0.01"
                        className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="Ej. 1.5"
                        value={ppmPctInput}
                        onChange={(e) => setPpmPctInput(e.target.value)}
                      />
                    </label>
                    <p className="text-[11px] text-slate-500">
                      {modalComplementos.ppm?.tasaFuente === 'CONFIG_EMPRESA'
                        ? 'Usa la tasa guardada en configuración de la empresa.'
                        : modalComplementos.ppm?.ratesCount > 0
                          ? 'También puede definir tasas con vigencia en Acciones → Configuración.'
                          : 'Se guarda en la configuración de la empresa para futuros informes.'}
                    </p>
                    <label className="block text-xs text-slate-600">
                      Monto PPM fijo (opcional, reemplaza tasa × base)
                      <input
                        type="number"
                        className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                        placeholder="Vacío = calcular con tasa"
                        value={ppmMontoInput}
                        onChange={(e) => setPpmMontoInput(e.target.value)}
                      />
                    </label>
                  </div>
                )}
                {modalComplementos?.remanente504?.status !== 'CONFIRMED' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Remanente 504 manual (solo si aún no hay propuesta SII)
                    </label>
                    <input
                      type="number"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder="Opcional"
                      value={rem504Input}
                      onChange={(e) => setRem504Input(e.target.value)}
                    />
                  </div>
                )}
                {modalComplementos?.iusc?.enabled !== false && (
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Impuesto único (cód. 48) — {MESES[mes]} {anio}
                    </label>
                    <input
                      type="number"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      placeholder="Opcional — pendiente si lo deja vacío"
                      value={iuscInput}
                      onChange={(e) => setIuscInput(e.target.value)}
                    />
                    {modalComplementos.iusc.previousReference != null && (
                      <p className="text-xs text-slate-500 mt-1">
                        Referencia mes anterior: {fmtMonto(modalComplementos.iusc.previousReference)} (no se copia al
                        total)
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </InformeIvaModal>

      {detalleRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 text-sm">
            <h2 className="text-lg font-semibold mb-2">Qué pasó</h2>
            {detalleRun.diagnostics?.userMessage && (
              <p className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-900 mb-4">
                {detalleRun.diagnostics.userMessage}
              </p>
            )}
            {detalleRun.diagnostics?.suggestedActions?.length > 0 && (
              <div className="mb-4">
                <p className="font-medium mb-2">Qué puede hacer:</p>
                <ul className="list-disc pl-5 space-y-1 text-slate-700">
                  {detalleRun.diagnostics.suggestedActions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </div>
            )}
            {detalleRun.run?.logEntries?.length > 0 && (
              <details className="mb-4">
                <summary className="cursor-pointer text-slate-600">Log técnico</summary>
                <ul className="mt-2 text-xs font-mono space-y-0.5 max-h-40 overflow-y-auto border rounded p-2 bg-slate-50">
                  {detalleRun.run.logEntries.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </details>
            )}
            <button
              type="button"
              className="w-full py-2 border rounded-lg"
              onClick={() => setDetalleRun(null)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {configCliente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">Cliente — envío de informe</h2>
            <p className="text-xs text-slate-500 mb-3">
              Abra desde la lista: <strong>Más opciones → Email del cliente y opciones avanzadas</strong>
            </p>
            <label className="block text-sm mb-1">Email donde se envía el informe</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
              value={configCliente.emailCliente || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, emailCliente: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={Boolean(configCliente.activoInformes)}
                onChange={(e) => setConfigCliente({ ...configCliente, activoInformes: e.target.checked })}
              />
              Incluir en informes IVA
            </label>
            <p className="text-xs text-slate-500 mb-3">
              El remanente 504 se obtiene automáticamente de la propuesta F29 del SII. Solo use el campo legacy abajo si
              aún no hay sync SII (primera vez); lo ideal es ingresarlo en «Más opciones → Datos complementarios» por
              período.
            </p>
            <label className="block text-sm mb-1">Remanente legacy (bootstrap global, evitar)</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              placeholder="Vacío = automático SII"
              value={configCliente.remanenteManual ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  remanenteManual: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <h3 className="text-sm font-semibold mt-2 mb-2">PPM y proyección F29</h3>
            <label className="block text-sm mb-1">Base del PPM</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.ppmBasisMode || 'RCV_NET_SALES'}
              onChange={(e) => setConfigCliente({ ...configCliente, ppmBasisMode: e.target.value })}
            >
              <option value="SII_PROPOSAL">Propuesta F29 SII (cód. 563)</option>
              <option value="RCV_NET_SALES">Ventas netas RCV (estimación)</option>
              <option value="MANUAL">Base manual</option>
              <option value="ACCOUNTING_GROSS_INCOME">Ingresos contabilidad (próximamente)</option>
            </select>
            {configCliente.ppmBasisMode === 'MANUAL' && (
              <input
                type="number"
                className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
                placeholder="Base PPM en pesos"
                value={configCliente.ppmBasisManual ?? ''}
                onChange={(e) =>
                  setConfigCliente({
                    ...configCliente,
                    ppmBasisManual: e.target.value === '' ? null : Number(e.target.value)
                  })
                }
              />
            )}
            <p className="text-xs text-slate-500 mb-2">
              Historial de tasa PPM (vigencia por fechas — un informe de septiembre usa la tasa vigente ese mes):
            </p>
            <ul className="text-xs border rounded-lg p-2 mb-2 max-h-28 overflow-y-auto space-y-1">
              {ppmRates.length === 0 && <li className="text-slate-500">Sin tasas — use fallback abajo o agregue una.</li>}
              {ppmRates.map((r) => (
                <li key={r._id} className="flex justify-between gap-2">
                  <span>
                    {r.tasa}% · {new Date(r.vigenciaDesde).toLocaleDateString('es-CL')}
                    {r.vigenciaHasta ? ` → ${new Date(r.vigenciaHasta).toLocaleDateString('es-CL')}` : ' → vigente'}
                  </span>
                  <button
                    type="button"
                    className="text-red-600 underline"
                    onClick={async () => {
                      await api.delete(`/informes-iva/ppm-rates/${r._id}`);
                      setPpmRates((list) => list.filter((x) => x._id !== r._id));
                    }}
                  >
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
              <input
                type="number"
                step="0.01"
                placeholder="Tasa %"
                className="border rounded px-2 py-1"
                value={nuevaTasaPpm.tasa}
                onChange={(e) => setNuevaTasaPpm((p) => ({ ...p, tasa: e.target.value }))}
              />
              <input
                type="date"
                className="border rounded px-2 py-1"
                value={nuevaTasaPpm.desde}
                onChange={(e) => setNuevaTasaPpm((p) => ({ ...p, desde: e.target.value }))}
              />
              <input
                type="date"
                className="border rounded px-2 py-1"
                placeholder="Hasta"
                value={nuevaTasaPpm.hasta}
                onChange={(e) => setNuevaTasaPpm((p) => ({ ...p, hasta: e.target.value }))}
              />
            </div>
            <button
              type="button"
              className="text-xs text-indigo-700 underline mb-3"
              onClick={async () => {
                if (!nuevaTasaPpm.tasa || !nuevaTasaPpm.desde) {
                  toast.error('Indique tasa y fecha desde');
                  return;
                }
                const { data } = await api.post(`/informes-iva/ppm-rates/${configCliente.clienteId}`, {
                  tasa: Number(nuevaTasaPpm.tasa),
                  vigenciaDesde: nuevaTasaPpm.desde,
                  vigenciaHasta: nuevaTasaPpm.hasta || null,
                  fuente: 'CONTADOR'
                });
                setPpmRates((list) => [data, ...list]);
                setNuevaTasaPpm({ tasa: '', desde: '', hasta: '' });
                toast.success('Tasa PPM agregada');
              }}
            >
              + Agregar vigencia de tasa
            </button>
            <label className="block text-sm mb-1">Fallback tasa % (si no hay vigencia para el mes)</label>
            <input
              type="number"
              step="0.01"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              placeholder="Ej. 1.5"
              value={configCliente.ppmPorcentaje ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  ppmPorcentaje: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <label className="block text-sm mb-1">PPM monto fijo (opcional, prioridad sobre tasa)</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              value={configCliente.ppmMontoManual ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  ppmMontoManual: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <div className="flex flex-col gap-2 text-sm mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={configCliente.monthlyTaxConfig?.ppm !== false}
                  onChange={(e) =>
                    setConfigCliente({
                      ...configCliente,
                      monthlyTaxConfig: { ...configCliente.monthlyTaxConfig, ppm: e.target.checked }
                    })
                  }
                />
                Incluir PPM en total F29
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={configCliente.monthlyTaxConfig?.honorariosRetention !== false}
                  onChange={(e) =>
                    setConfigCliente({
                      ...configCliente,
                      monthlyTaxConfig: {
                        ...configCliente.monthlyTaxConfig,
                        honorariosRetention: e.target.checked
                      }
                    })
                  }
                />
                Retención honorarios (cód. 151)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={configCliente.monthlyTaxConfig?.iusc !== false}
                  onChange={(e) =>
                    setConfigCliente({
                      ...configCliente,
                      monthlyTaxConfig: { ...configCliente.monthlyTaxConfig, iusc: e.target.checked }
                    })
                  }
                />
                Incluir impuesto único (cód. 48) en el informe
              </label>
            </div>
            <label className="block text-sm mb-1">Nota en el informe (opcional)</label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4 min-h-[60px]"
              value={configCliente.notaContador || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, notaContador: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 text-sm border rounded-lg" onClick={() => setConfigCliente(null)}>
                Cerrar
              </button>
              <button type="button" className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg" onClick={guardarConfig}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
